import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  BusinessOperationIdSchema,
  type BusinessOperationTraceV1,
  BusinessOperationTraceV1Schema,
  isBusinessOperationTraceV1,
} from "./business-operation.js";
import {
  isPurchaseOrderDraftRecord,
  PurchaseOrderDraftLineRecordSchema,
  PurchaseOrderSupplierSnapshotSchema,
} from "./procurement.js";
import { derivePurchaseOrderNumber, PurchaseOrderNumberSchema } from "./purchase-order-number.js";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
});
const CanonicalTextPattern = "^(?:\\S|\\S[\\s\\S]*\\S)$";
const CanonicalTextSchema = (maximum: number) =>
  Type.String({ minLength: 1, maxLength: maximum, pattern: CanonicalTextPattern });
const NullableCanonicalTextSchema = (maximum: number) =>
  Type.Union([CanonicalTextSchema(maximum), Type.Null()]);
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const TimestampSchema = Type.String({ minLength: 20, maxLength: 64 });
const VersionSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const AmountFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const Sha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const IdempotencyKeySchema = BusinessOperationIdSchema;
const ReasonSchema = CanonicalTextSchema(200);
const ExplanationListSchema = Type.Array(CanonicalTextSchema(300), {
  minItems: 1,
  maxItems: 8,
  uniqueItems: true,
});

export const PreviewPurchaseOrderApprovalRequestSchema = Type.Object(
  {
    purchaseOrderId: StableIdSchema,
    expectedPurchaseOrderVersion: VersionSchema,
    reason: ReasonSchema,
  },
  { additionalProperties: false },
);
export type PreviewPurchaseOrderApprovalRequest = Static<
  typeof PreviewPurchaseOrderApprovalRequestSchema
>;

export const PurchaseOrderApprovalPreviewReadModelSchema = Type.Object(
  {
    kind: Type.Literal("PURCHASE_ORDER_APPROVAL"),
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    purchaseOrderVersion: VersionSchema,
    purchaseOrderStatus: Type.Literal("DRAFT"),
    businessDate: BusinessDateSchema,
    supplierCode: Type.String({
      minLength: 1,
      maxLength: 40,
      pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
    }),
    supplierName: CanonicalTextSchema(80),
    expectedDeliveryDate: Type.Union([BusinessDateSchema, Type.Null()]),
    lineCount: Type.Integer({ minimum: 1, maximum: 200 }),
    subtotalFen: AmountFenSchema,
    taxFen: AmountFenSchema,
    totalFen: AmountFenSchema,
    reason: ReasonSchema,
    sourceReferenceSha256: Sha256Schema,
    businessFactSha256: Sha256Schema,
    originalDocumentLabel: CanonicalTextSchema(120),
    consequences: ExplanationListSchema,
    nextSteps: ExplanationListSchema,
    requirement: Type.Literal("MANAGER_OR_OWNER"),
    approverLabel: CanonicalTextSchema(120),
    canSubmit: Type.Boolean(),
    blocker: Type.Union([CanonicalTextSchema(300), Type.Null()]),
    generatedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderApprovalPreviewReadModel = Static<
  typeof PurchaseOrderApprovalPreviewReadModelSchema
>;

export const CreatePurchaseOrderApprovalCommandSchema = Type.Object(
  {
    purchaseOrderId: StableIdSchema,
    expectedPurchaseOrderVersion: VersionSchema,
    reason: ReasonSchema,
    sourceReferenceSha256: Sha256Schema,
    businessFactSha256: Sha256Schema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreatePurchaseOrderApprovalCommand = Static<
  typeof CreatePurchaseOrderApprovalCommandSchema
>;

const ApprovalIdSchema = Type.String({
  minLength: 8,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._-]{7,127}$",
});

export const PurchaseOrderApprovalSubmissionRequestSchema = Type.Object(
  {
    approvalId: ApprovalIdSchema,
    command: CreatePurchaseOrderApprovalCommandSchema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderApprovalSubmissionRequest = Static<
  typeof PurchaseOrderApprovalSubmissionRequestSchema
>;

const PurchaseOrderApprovalSourceSnapshotFields = {
  kind: Type.Literal("PURCHASE_ORDER_APPROVAL"),
  snapshotVersion: Type.Literal(1),
  purchaseOrderId: StableIdSchema,
  purchaseOrderNumber: PurchaseOrderNumberSchema,
  purchaseOrderVersion: VersionSchema,
  purchaseOrderStatus: Type.Literal("DRAFT"),
  businessDate: BusinessDateSchema,
  supplier: PurchaseOrderSupplierSnapshotSchema,
  expectedDeliveryDate: Type.Union([BusinessDateSchema, Type.Null()]),
  notes: NullableCanonicalTextSchema(500),
  lines: Type.Array(PurchaseOrderDraftLineRecordSchema, { minItems: 1, maxItems: 200 }),
  subtotalFen: AmountFenSchema,
  taxFen: AmountFenSchema,
  totalFen: AmountFenSchema,
  inventoryEffect: Type.Literal("NONE"),
  payableEffect: Type.Literal("NONE"),
  reason: ReasonSchema,
  originalDocumentLabel: CanonicalTextSchema(120),
  consequences: ExplanationListSchema,
  nextSteps: ExplanationListSchema,
  sourceReferenceSha256: Sha256Schema,
  businessFactSha256: Sha256Schema,
  sourceOperationTrace: BusinessOperationTraceV1Schema,
  capturedAt: TimestampSchema,
};

export const PurchaseOrderApprovalSourceSnapshotSchema = Type.Object(
  PurchaseOrderApprovalSourceSnapshotFields,
  { additionalProperties: false },
);
export type PurchaseOrderApprovalSourceSnapshot = Static<
  typeof PurchaseOrderApprovalSourceSnapshotSchema
>;

export const PurchaseOrderApprovalSnapshotSchema = Type.Object(
  {
    ...PurchaseOrderApprovalSourceSnapshotFields,
    commandSha256: Sha256Schema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderApprovalSnapshot = Static<typeof PurchaseOrderApprovalSnapshotSchema>;

export const PurchaseOrderApprovalExecutionBindingSchema = Type.Object(
  {
    bindingVersion: Type.Literal(1),
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    purchaseOrderVersion: VersionSchema,
    lastCommandId: BusinessOperationIdSchema,
    sourceOperationTrace: BusinessOperationTraceV1Schema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderApprovalExecutionBinding = Static<
  typeof PurchaseOrderApprovalExecutionBindingSchema
>;

export const PurchaseOrderApprovalPreparationSchema = Type.Object(
  {
    preview: PurchaseOrderApprovalPreviewReadModelSchema,
    snapshot: PurchaseOrderApprovalSourceSnapshotSchema,
    executionBinding: PurchaseOrderApprovalExecutionBindingSchema,
    businessFactSha256: Sha256Schema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderApprovalPreparation = Static<
  typeof PurchaseOrderApprovalPreparationSchema
>;

export const PurchaseOrderApprovalTransitionSchema = Type.Object(
  {
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    sourceVersion: VersionSchema,
    pendingVersion: VersionSchema,
    status: Type.Literal("PENDING_APPROVAL"),
    inventoryEffect: Type.Literal("NONE"),
    payableEffect: Type.Literal("NONE"),
    operationTrace: BusinessOperationTraceV1Schema,
    transitionedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderApprovalTransition = Static<typeof PurchaseOrderApprovalTransitionSchema>;

export const PurchaseOrderApprovalSubmissionReceiptSchema = Type.Object(
  {
    approvalId: ApprovalIdSchema,
    command: CreatePurchaseOrderApprovalCommandSchema,
    preparation: PurchaseOrderApprovalPreparationSchema,
    snapshot: PurchaseOrderApprovalSnapshotSchema,
    snapshotSha256: Sha256Schema,
    transition: PurchaseOrderApprovalTransitionSchema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderApprovalSubmissionReceipt = Static<
  typeof PurchaseOrderApprovalSubmissionReceiptSchema
>;

export const PurchaseOrderApprovalDecisionRequestSchema = Type.Object(
  {
    approvalId: ApprovalIdSchema,
    purchaseOrderId: StableIdSchema,
    expectedPendingVersion: VersionSchema,
    approvalSnapshotSha256: Sha256Schema,
    applicantAccountId: StableIdSchema,
    decision: Type.Union([Type.Literal("APPROVE"), Type.Literal("REJECT")]),
    reason: ReasonSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderApprovalDecisionRequest = Static<
  typeof PurchaseOrderApprovalDecisionRequestSchema
>;

export const PurchaseOrderApprovalDecisionReceiptSchema = Type.Object(
  {
    request: PurchaseOrderApprovalDecisionRequestSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    decidedVersion: VersionSchema,
    status: Type.Union([Type.Literal("APPROVED"), Type.Literal("REJECTED")]),
    inventoryEffect: Type.Literal("NONE"),
    payableEffect: Type.Literal("NONE"),
    operationTrace: BusinessOperationTraceV1Schema,
    decidedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderApprovalDecisionReceipt = Static<
  typeof PurchaseOrderApprovalDecisionReceiptSchema
>;

function isCanonicalBusinessDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function isPurchaseOrderNumberForSource(
  purchaseOrderNumber: string,
  purchaseOrderId: string,
  businessDate: string,
): boolean {
  try {
    return derivePurchaseOrderNumber(businessDate, purchaseOrderId) === purchaseOrderNumber;
  } catch {
    return false;
  }
}

function tracesAreEqual(left: BusinessOperationTraceV1, right: BusinessOperationTraceV1): boolean {
  return (
    left.contractVersion === right.contractVersion &&
    left.commandId === right.commandId &&
    left.correlationId === right.correlationId &&
    left.causationId === right.causationId &&
    left.idempotencyKey === right.idempotencyKey &&
    left.businessNumber?.kind === right.businessNumber?.kind &&
    left.businessNumber?.value === right.businessNumber?.value
  );
}

function traceMatchesPurchaseOrder(
  trace: BusinessOperationTraceV1,
  purchaseOrderId: string,
  purchaseOrderNumber: string,
): boolean {
  return (
    isBusinessOperationTraceV1(trace) &&
    trace.correlationId === `purchase-order:${purchaseOrderId}` &&
    trace.businessNumber?.kind === "PURCHASE_ORDER" &&
    trace.businessNumber.value === purchaseOrderNumber
  );
}

export function isPreviewPurchaseOrderApprovalRequest(
  value: unknown,
): value is PreviewPurchaseOrderApprovalRequest {
  return Value.Check(PreviewPurchaseOrderApprovalRequestSchema, value);
}

export function isCreatePurchaseOrderApprovalCommand(
  value: unknown,
): value is CreatePurchaseOrderApprovalCommand {
  return Value.Check(CreatePurchaseOrderApprovalCommandSchema, value);
}

export function isPurchaseOrderApprovalSubmissionRequest(
  value: unknown,
): value is PurchaseOrderApprovalSubmissionRequest {
  return (
    Value.Check(PurchaseOrderApprovalSubmissionRequestSchema, value) &&
    isCreatePurchaseOrderApprovalCommand(value.command)
  );
}

export function isPurchaseOrderApprovalPreviewReadModel(
  value: unknown,
): value is PurchaseOrderApprovalPreviewReadModel {
  if (!Value.Check(PurchaseOrderApprovalPreviewReadModelSchema, value)) return false;
  const preview = value as PurchaseOrderApprovalPreviewReadModel;
  return (
    isCanonicalBusinessDate(preview.businessDate) &&
    (preview.expectedDeliveryDate === null ||
      (isCanonicalBusinessDate(preview.expectedDeliveryDate) &&
        preview.expectedDeliveryDate >= preview.businessDate)) &&
    isCanonicalTimestamp(preview.generatedAt) &&
    isPurchaseOrderNumberForSource(
      preview.purchaseOrderNumber,
      preview.purchaseOrderId,
      preview.businessDate,
    ) &&
    preview.originalDocumentLabel === `采购单 ${preview.purchaseOrderNumber}` &&
    Number.isSafeInteger(preview.subtotalFen + preview.taxFen) &&
    preview.totalFen === preview.subtotalFen + preview.taxFen &&
    (preview.canSubmit ? preview.blocker === null : preview.blocker !== null)
  );
}

function purchaseOrderApprovalSourceSnapshotIsConsistent(
  snapshot: PurchaseOrderApprovalSourceSnapshot,
): boolean {
  if (
    !isCanonicalTimestamp(snapshot.capturedAt) ||
    !traceMatchesPurchaseOrder(
      snapshot.sourceOperationTrace,
      snapshot.purchaseOrderId,
      snapshot.purchaseOrderNumber,
    ) ||
    snapshot.originalDocumentLabel !== `采购单 ${snapshot.purchaseOrderNumber}`
  ) {
    return false;
  }
  return isPurchaseOrderDraftRecord({
    purchaseOrderId: snapshot.purchaseOrderId,
    purchaseOrderNumber: snapshot.purchaseOrderNumber,
    businessDate: snapshot.businessDate,
    status: snapshot.purchaseOrderStatus,
    supplier: snapshot.supplier,
    expectedDeliveryDate: snapshot.expectedDeliveryDate,
    notes: snapshot.notes,
    lines: snapshot.lines,
    subtotalFen: snapshot.subtotalFen,
    taxFen: snapshot.taxFen,
    totalFen: snapshot.totalFen,
    inventoryEffect: snapshot.inventoryEffect,
    payableEffect: snapshot.payableEffect,
    version: snapshot.purchaseOrderVersion,
    lastCommandId: snapshot.sourceOperationTrace.commandId,
    operationTrace: snapshot.sourceOperationTrace,
    createdAt: snapshot.capturedAt,
    updatedAt: snapshot.capturedAt,
    evidence: "SIMULATED",
  });
}

export function isPurchaseOrderApprovalSourceSnapshot(
  value: unknown,
): value is PurchaseOrderApprovalSourceSnapshot {
  return (
    Value.Check(PurchaseOrderApprovalSourceSnapshotSchema, value) &&
    purchaseOrderApprovalSourceSnapshotIsConsistent(value)
  );
}

export function isPurchaseOrderApprovalSnapshot(
  value: unknown,
): value is PurchaseOrderApprovalSnapshot {
  return (
    Value.Check(PurchaseOrderApprovalSnapshotSchema, value) &&
    purchaseOrderApprovalSourceSnapshotIsConsistent(value)
  );
}

export function isPurchaseOrderApprovalPreparation(
  value: unknown,
): value is PurchaseOrderApprovalPreparation {
  if (!Value.Check(PurchaseOrderApprovalPreparationSchema, value)) return false;
  const preparation = value as PurchaseOrderApprovalPreparation;
  const { preview, snapshot, executionBinding: binding } = preparation;
  if (
    !isPurchaseOrderApprovalPreviewReadModel(preview) ||
    !isPurchaseOrderApprovalSourceSnapshot(snapshot) ||
    !isBusinessOperationTraceV1(binding.sourceOperationTrace)
  ) {
    return false;
  }
  return (
    preparation.businessFactSha256 === preview.businessFactSha256 &&
    preparation.businessFactSha256 === snapshot.businessFactSha256 &&
    preview.purchaseOrderId === snapshot.purchaseOrderId &&
    preview.purchaseOrderId === binding.purchaseOrderId &&
    preview.purchaseOrderNumber === snapshot.purchaseOrderNumber &&
    preview.purchaseOrderNumber === binding.purchaseOrderNumber &&
    preview.purchaseOrderVersion === snapshot.purchaseOrderVersion &&
    preview.purchaseOrderVersion === binding.purchaseOrderVersion &&
    preview.purchaseOrderStatus === snapshot.purchaseOrderStatus &&
    preview.businessDate === snapshot.businessDate &&
    preview.supplierCode === snapshot.supplier.supplierCode &&
    preview.supplierName === snapshot.supplier.name &&
    preview.expectedDeliveryDate === snapshot.expectedDeliveryDate &&
    preview.lineCount === snapshot.lines.length &&
    preview.subtotalFen === snapshot.subtotalFen &&
    preview.taxFen === snapshot.taxFen &&
    preview.totalFen === snapshot.totalFen &&
    preview.reason === snapshot.reason &&
    preview.sourceReferenceSha256 === snapshot.sourceReferenceSha256 &&
    preview.originalDocumentLabel === snapshot.originalDocumentLabel &&
    preview.consequences.every((item, index) => item === snapshot.consequences[index]) &&
    preview.nextSteps.every((item, index) => item === snapshot.nextSteps[index]) &&
    preview.consequences.length === snapshot.consequences.length &&
    preview.nextSteps.length === snapshot.nextSteps.length &&
    preview.generatedAt === snapshot.capturedAt &&
    binding.lastCommandId === binding.sourceOperationTrace.commandId &&
    binding.lastCommandId === snapshot.sourceOperationTrace.commandId &&
    tracesAreEqual(binding.sourceOperationTrace, snapshot.sourceOperationTrace)
  );
}

export function isPurchaseOrderApprovalSubmissionReceipt(
  value: unknown,
): value is PurchaseOrderApprovalSubmissionReceipt {
  if (!Value.Check(PurchaseOrderApprovalSubmissionReceiptSchema, value)) return false;
  const receipt = value as PurchaseOrderApprovalSubmissionReceipt;
  const { command, preparation, snapshot, transition } = receipt;
  const sourceSnapshot = preparation.snapshot;
  if (
    !isCreatePurchaseOrderApprovalCommand(command) ||
    !isPurchaseOrderApprovalPreparation(preparation) ||
    !isPurchaseOrderApprovalSnapshot(snapshot) ||
    !isBusinessOperationTraceV1(transition.operationTrace) ||
    !isCanonicalTimestamp(transition.transitionedAt)
  ) {
    return false;
  }
  const { commandSha256: _commandSha256, ...snapshotSource } = snapshot;
  return (
    JSON.stringify(snapshotSource) === JSON.stringify(sourceSnapshot) &&
    command.purchaseOrderId === preparation.preview.purchaseOrderId &&
    command.expectedPurchaseOrderVersion === preparation.preview.purchaseOrderVersion &&
    command.reason === preparation.preview.reason &&
    command.sourceReferenceSha256 === preparation.preview.sourceReferenceSha256 &&
    command.businessFactSha256 === preparation.preview.businessFactSha256 &&
    transition.purchaseOrderId === command.purchaseOrderId &&
    transition.purchaseOrderNumber === preparation.preview.purchaseOrderNumber &&
    transition.sourceVersion === command.expectedPurchaseOrderVersion &&
    transition.pendingVersion === transition.sourceVersion + 1 &&
    transition.operationTrace.commandId === command.idempotencyKey &&
    transition.operationTrace.idempotencyKey === command.idempotencyKey &&
    transition.operationTrace.correlationId === sourceSnapshot.sourceOperationTrace.correlationId &&
    transition.operationTrace.causationId === sourceSnapshot.sourceOperationTrace.commandId &&
    transition.operationTrace.businessNumber?.kind === "PURCHASE_ORDER" &&
    transition.operationTrace.businessNumber.value === transition.purchaseOrderNumber
  );
}

export function isPurchaseOrderApprovalDecisionRequest(
  value: unknown,
): value is PurchaseOrderApprovalDecisionRequest {
  return Value.Check(PurchaseOrderApprovalDecisionRequestSchema, value);
}

export function isPurchaseOrderApprovalDecisionReceipt(
  value: unknown,
): value is PurchaseOrderApprovalDecisionReceipt {
  if (!Value.Check(PurchaseOrderApprovalDecisionReceiptSchema, value)) return false;
  const receipt = value as PurchaseOrderApprovalDecisionReceipt;
  const { request, operationTrace } = receipt;
  return (
    isPurchaseOrderApprovalDecisionRequest(request) &&
    isCanonicalTimestamp(receipt.decidedAt) &&
    receipt.decidedVersion === request.expectedPendingVersion + 1 &&
    receipt.status === (request.decision === "APPROVE" ? "APPROVED" : "REJECTED") &&
    traceMatchesPurchaseOrder(
      operationTrace,
      request.purchaseOrderId,
      receipt.purchaseOrderNumber,
    ) &&
    operationTrace.commandId === request.idempotencyKey &&
    operationTrace.idempotencyKey === request.idempotencyKey &&
    operationTrace.causationId !== null
  );
}

/**
 * One canonical source-fact projection for producers and trusted consumers.
 * The digest itself stays outside this browser-safe contracts package.
 */
export function purchaseOrderApprovalBusinessFacts(
  snapshot: PurchaseOrderApprovalSourceSnapshot,
): object {
  return {
    purchaseOrderId: snapshot.purchaseOrderId,
    purchaseOrderNumber: snapshot.purchaseOrderNumber,
    purchaseOrderVersion: snapshot.purchaseOrderVersion,
    purchaseOrderStatus: snapshot.purchaseOrderStatus,
    businessDate: snapshot.businessDate,
    supplier: snapshot.supplier,
    expectedDeliveryDate: snapshot.expectedDeliveryDate,
    notes: snapshot.notes,
    lines: snapshot.lines,
    subtotalFen: snapshot.subtotalFen,
    taxFen: snapshot.taxFen,
    totalFen: snapshot.totalFen,
    inventoryEffect: snapshot.inventoryEffect,
    payableEffect: snapshot.payableEffect,
    reason: snapshot.reason,
    sourceReferenceSha256: snapshot.sourceReferenceSha256,
    sourceOperationTrace: snapshot.sourceOperationTrace,
  };
}
